import config from '@/assets/whiteLabel.json';

const DEFAULT_BRAND_KEY = 'securmeet';

const escapeRegex = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const envActiveProject = (process.env.VUE_APP_ACTIVE_PROJECT || '').toLowerCase();
const configActiveProject = (config.activeProject || '').toLowerCase();

export const getBrandConfig = () => config;

export const getActiveBrandKey = () => envActiveProject || configActiveProject || DEFAULT_BRAND_KEY;

export const getBrand = key => {
  const normalizedKey = key?.toLowerCase?.();
  if (!normalizedKey) return config?.brands?.[DEFAULT_BRAND_KEY] || null;
  if (!config?.brands) return null;
  if (config.brands[normalizedKey]) return config.brands[normalizedKey];
  return config.brands[DEFAULT_BRAND_KEY] || null;
};

export const getActiveBrand = () => getBrand(getActiveBrandKey());

export const getBrandName = () => getActiveBrand()?.name || getBrand(DEFAULT_BRAND_KEY)?.name || 'SecurMeet';

export const getBrandTheme = () => getActiveBrand()?.theme || getBrand(DEFAULT_BRAND_KEY)?.theme || 'green';

export const getBrandColors = () => ({
  ...(getBrand(DEFAULT_BRAND_KEY)?.colors || {}),
  ...(getActiveBrand()?.colors || {})
});

export const getBrandDomain = () => getActiveBrand()?.domain || getBrand(DEFAULT_BRAND_KEY)?.domain || 'securmeet.com';

export const applyBrandColors = targetDocument => {
  const doc = targetDocument || (typeof document !== 'undefined' ? document : null);
  if (!doc) return;
  const root = doc.documentElement;
  const colors = getBrandColors();
  Object.entries(colors).forEach(([key, value]) => {
    root.style.setProperty(`--${key}`, value);
  });
};

const resolveLogo = (logos, type, theme) => {
  if (!logos) return null;
  const entry = logos[type];
  if (!entry) return null;
  if (typeof entry === 'string') return entry;
  if (typeof entry === 'object') {
    if (theme && entry[theme]) return entry[theme];
    return entry.default || Object.values(entry)[0];
  }
  return null;
};

export const getLogo = (type, theme) => {
  const brand = getActiveBrand();
  const brandLogo = resolveLogo(brand?.logos, type, theme);
  if (brandLogo) return brandLogo;
  const fallback = getBrand(DEFAULT_BRAND_KEY);
  const fallbackLogo = resolveLogo(fallback?.logos, type, theme);
  if (fallbackLogo) return fallbackLogo;
  return '/img/static/logo-landing.svg';
};

export const getAllLogos = () => {
  const brand = getActiveBrand();
  return {
    ...(getBrand(DEFAULT_BRAND_KEY)?.logos || {}),
    ...(brand?.logos || {})
  };
};

export const setFavicon = targetDocument => {
  const doc = targetDocument || (typeof document !== 'undefined' ? document : null);
  if (!doc) return;
  const faviconPath = getLogo('favicon');
  if (!faviconPath) return;
  const link = doc.querySelector('link[rel="icon"]') || doc.createElement('link');
  link.rel = 'icon';
  link.href = faviconPath;
  if (!link.parentNode) doc.head.appendChild(link);
};

const replaceDefaultBrandName = (input, brandName) => {
  const defaultBrand = getBrand(DEFAULT_BRAND_KEY)?.name || 'SecurMeet';
  if (!input || typeof input !== 'string') return input;
  if (!defaultBrand || defaultBrand === brandName) return input;
  const regex = new RegExp(escapeRegex(defaultBrand), 'gi');
  return input.replace(regex, match => {
    if (match === match.toUpperCase()) return brandName.toUpperCase();
    if (match[0] === match[0].toLowerCase()) return brandName.toLowerCase();
    return brandName;
  });
};

export const replaceBrandTokens = value => {
  const brandName = getBrandName();
  if (Array.isArray(value)) return value.map(item => replaceBrandTokens(item));
  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, val]) => {
      acc[key] = replaceBrandTokens(val);
      return acc;
    }, Array.isArray(value) ? [] : {});
  }
  const tokenReplaced = typeof value === 'string' ? value.replace(/{{\s*brand\s*}}/gi, brandName) : value;
  return replaceDefaultBrandName(tokenReplaced, brandName);
};

export const initializeBranding = targetDocument => {
  applyBrandColors(targetDocument);
  setFavicon(targetDocument);
};


